import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { DateTime, Num, T, useGT, useMessages, Var, msg } from 'gt-react';
import {
  AlarmIcon,
  ArrowFatLinesUpIcon,
  DotsThreeVerticalIcon,
  SunIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import type { AgentTaskQueueItem, TaskPriority } from '@erp/rpc';
import {
  useAgentTaskQueue,
  useClearQueuedTaskSnooze,
  useSetQueuedTaskPriority,
} from 'src/api/rpc/hooks/tasks/useAgentTaskQueue';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { toast } from 'src/components/ui/toast';
import { useCan } from 'src/hooks/useCan';
import { PriorityDot } from '../../inbox/components/helpers';
import { SnoozeTaskDialog } from '../../tasks/SnoozeTaskDialog';
import {
  QUEUE_PRIORITIES,
  isAlreadyNext,
  procedureLockState,
} from './agentTaskQueue';

const PRIORITY_LABELS = {
  urgent: msg('Urgent', { context: 'task priority level' }),
  high: msg('High', { context: 'task priority level' }),
  medium: msg('Medium', { context: 'task priority level' }),
  low: msg('Low', { context: 'task priority level' }),
} satisfies Record<TaskPriority, string>;

type QueueSection = 'upcoming' | 'snoozed' | 'inProgress';

/**
 * The agent's queue in claim order: what it is working on, what it takes
 * next, and what wakes later. Order comes from the server's claim ordering,
 * so this list is the order the agent works, not a client-side sort.
 */
export function AgentTaskQueuePanel({
  agentDefinitionId,
}: {
  agentDefinitionId: string;
}) {
  const query = useAgentTaskQueue(agentDefinitionId);
  const [canUpdate] = useCan(Action.Update, Subject.Task);
  const [snoozing, setSnoozing] = useState<string | null>(null);

  if (query.isPending)
    return (
      <div className="flex flex-col gap-2" data-testid="agent-task-queue">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  if (query.isError)
    return (
      <p className="text-sm text-destructive" data-testid="agent-task-queue">
        <T context="agent page queue pane, the queue failed to load">
          Could not load the queue.
        </T>
      </p>
    );

  const queue = query.data;
  const empty =
    queue.inProgress.length === 0 &&
    queue.upcomingTotal === 0 &&
    queue.snoozedTotal === 0;

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-1"
      data-testid="agent-task-queue"
    >
      <p className="text-xs text-muted-foreground">
        <T context="agent page queue pane explainer">
          The agent takes tasks top to bottom: highest priority first, then the
          oldest. Snoozed tasks join the queue when they wake.
        </T>
      </p>

      {empty && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          <T context="agent page queue pane, nothing queued">
            Nothing is queued for this agent.
          </T>
        </p>
      )}

      {queue.inProgress.length > 0 && (
        <QueueTable
          title={
            <T context="agent page queue pane section: tasks the agent is working on now">
              Working now
            </T>
          }
          section="inProgress"
          rows={queue.inProgress}
          canUpdate={false}
          onSnooze={setSnoozing}
        />
      )}

      {queue.upcomingTotal > 0 && (
        <QueueTable
          title={
            <T context="agent page queue pane section: claimable tasks in claim order">
              Up next <Num>{queue.upcomingTotal}</Num>
            </T>
          }
          section="upcoming"
          rows={queue.upcoming}
          canUpdate={canUpdate}
          onSnooze={setSnoozing}
          footer={
            <QueueFooter
              shown={queue.upcoming.length}
              hidden={queue.upcomingHidden}
              total={queue.upcomingTotal}
            />
          }
        />
      )}

      {queue.snoozedTotal > 0 && (
        <QueueTable
          title={
            <T context="agent page queue pane section: snoozed tasks, soonest wake first">
              Waking later <Num>{queue.snoozedTotal}</Num>
            </T>
          }
          section="snoozed"
          rows={queue.snoozed}
          canUpdate={canUpdate}
          onSnooze={setSnoozing}
          footer={
            <QueueFooter
              shown={queue.snoozed.length}
              hidden={0}
              total={queue.snoozedTotal}
            />
          }
        />
      )}

      {snoozing && (
        <SnoozeTaskDialog
          taskIds={[snoozing]}
          open
          onOpenChange={(open) => {
            if (!open) setSnoozing(null);
          }}
        />
      )}
    </div>
  );
}

function QueueFooter({
  shown,
  hidden,
  total,
}: {
  shown: number;
  hidden: number;
  total: number;
}) {
  if (hidden === 0 && shown + hidden >= total) return null;
  return (
    <p className="px-2 pt-1 text-xs text-muted-foreground">
      {shown + hidden < total && (
        <T context="agent page queue pane footer when the list is truncated">
          Showing the first <Num>{shown + hidden}</Num> of <Num>{total}</Num>
          .{' '}
        </T>
      )}
      {hidden > 0 && (
        <T context="agent page queue pane footer: queued tasks the viewer has no access to still hold their place">
          <Num>{hidden}</Num> queued tasks you cannot see keep their place in
          line.
        </T>
      )}
    </p>
  );
}

function QueueTable({
  title,
  section,
  rows,
  canUpdate,
  onSnooze,
  footer,
}: {
  title: ReactNode;
  section: QueueSection;
  rows: readonly AgentTaskQueueItem[];
  canUpdate: boolean;
  onSnooze: (taskId: string) => void;
  footer?: ReactNode;
}) {
  return (
    <section data-testid={`agent-task-queue-${section}`}>
      <h3 className="mb-1 text-sm font-medium">{title}</h3>
      <Table density="compact">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-right">
              {section === 'upcoming' ? (
                <T context="column header: position in the agent's queue">#</T>
              ) : null}
            </TableHead>
            <TableHead>
              <T context="column header: task">Task</T>
            </TableHead>
            <TableHead className="w-28">
              <T context="column header: task priority">Priority</T>
            </TableHead>
            <TableHead className="w-40">
              {section === 'snoozed' ? (
                <T context="column header: when a snoozed task wakes">Wakes</T>
              ) : section === 'inProgress' ? (
                <T context="column header: when the agent started the task">
                  Started
                </T>
              ) : (
                <T context="column header: when the task was created">
                  Queued since
                </T>
              )}
            </TableHead>
            <TableHead className="w-40">
              <T context="column header: SOP version the task is locked to">
                SOP
              </T>
            </TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <QueueRow
              key={row.id}
              row={row}
              section={section}
              canUpdate={canUpdate}
              onSnooze={onSnooze}
            />
          ))}
        </TableBody>
      </Table>
      {footer}
    </section>
  );
}

function QueueRow({
  row,
  section,
  canUpdate,
  onSnooze,
}: {
  row: AgentTaskQueueItem;
  section: QueueSection;
  canUpdate: boolean;
  onSnooze: (taskId: string) => void;
}) {
  const m = useMessages();
  const when =
    section === 'snoozed'
      ? row.snoozedUntil
      : section === 'inProgress'
        ? row.startedAt
        : row.createdAt;
  return (
    <TableRow data-testid="agent-task-queue-row" data-task-id={row.id}>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {row.position != null && <Num>{row.position}</Num>}
      </TableCell>
      <TableCell className="max-w-0">
        <Link
          to="/tasks/$taskId"
          params={{ taskId: row.id }}
          className="flex min-w-0 items-baseline gap-2 hover:underline"
        >
          {row.displayId && (
            <span className="shrink-0 font-mono text-xs text-muted-foreground">
              {row.displayId}
            </span>
          )}
          <span className="truncate">{row.title}</span>
        </Link>
      </TableCell>
      <TableCell>
        <span className="flex items-center gap-1.5">
          <PriorityDot priority={row.priority} />
          {m(PRIORITY_LABELS[row.priority])}
        </span>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {when && (
          <DateTime options={{ dateStyle: 'medium', timeStyle: 'short' }}>
            {when}
          </DateTime>
        )}
      </TableCell>
      <TableCell>
        <ProcedureLocks locks={row.procedureLocks} />
      </TableCell>
      <TableCell>
        {canUpdate && section !== 'inProgress' && (
          <QueueRowMenu row={row} section={section} onSnooze={onSnooze} />
        )}
      </TableCell>
    </TableRow>
  );
}

function ProcedureLocks({
  locks,
}: {
  locks: AgentTaskQueueItem['procedureLocks'];
}) {
  if (locks.length === 0)
    return (
      <span className="text-xs text-muted-foreground">
        <T context="SOP column: the task has not locked an SOP version yet">
          Not locked
        </T>
      </span>
    );
  return (
    <span className="flex flex-wrap gap-1">
      {locks.map((lock) => {
        const state = procedureLockState(lock);
        return (
          <Badge
            key={lock.sopId}
            variant={state === 'behind' ? 'destructive' : 'secondary'}
            className="font-normal tabular-nums"
            title={lock.name}
          >
            {state === 'behind' ? (
              <T context="SOP badge: task locked to an older SOP version than the published one">
                v<Var>{lock.lockedVersion}</Var> · published v
                <Var>{lock.publishedVersion}</Var>
              </T>
            ) : (
              <T context="SOP badge: task locked to this SOP version">
                v<Var>{lock.lockedVersion}</Var>
              </T>
            )}
          </Badge>
        );
      })}
    </span>
  );
}

function QueueRowMenu({
  row,
  section,
  onSnooze,
}: {
  row: AgentTaskQueueItem;
  section: QueueSection;
  onSnooze: (taskId: string) => void;
}) {
  const gt = useGT();
  const m = useMessages();
  const setPriority = useSetQueuedTaskPriority();
  const clearSnooze = useClearQueuedTaskSnooze();

  function changePriority(priority: TaskPriority, success: string) {
    setPriority.mutate(
      { id: row.id, priority },
      {
        onSuccess: () => toast.success(success),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  function runNext() {
    const ahead = row.olderUrgentAhead;
    changePriority(
      'urgent',
      ahead === 0
        ? gt('Set to urgent. The agent takes it next.', {
            context: 'toast after Run next on a queued task',
          })
        : ahead === 1
          ? gt('Set to urgent. One older urgent task runs first.', {
              context: 'toast after Run next on a queued task',
            })
          : gt('Set to urgent. {count} older urgent tasks run first.', {
              context: 'toast after Run next on a queued task',
              count: String(ahead),
            }),
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          data-testid="agent-task-queue-row-menu"
          aria-label={gt('Reprioritize {title}', {
            context: 'queue row action menu button',
            title: row.title,
          })}
        >
          <DotsThreeVerticalIcon className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {section === 'upcoming' && (
          <DropdownMenuItem
            disabled={isAlreadyNext(row) || setPriority.isPending}
            onClick={runNext}
          >
            <ArrowFatLinesUpIcon className="mr-2 h-4 w-4" />
            <T context="queue row action: set the task to urgent so the agent takes it next">
              Run next
            </T>
          </DropdownMenuItem>
        )}
        {section === 'upcoming' ? (
          <DropdownMenuItem onClick={() => onSnooze(row.id)}>
            <AlarmIcon className="mr-2 h-4 w-4" />
            <T context="queue row action: hide the task from the agent until a time">
              Snooze…
            </T>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={clearSnooze.isPending}
            onClick={() =>
              clearSnooze.mutate(row.id, {
                onSuccess: () =>
                  toast.success(
                    gt('Snooze cleared. The task is back in the queue.', {
                      context: 'toast after unsnoozing a queued task',
                    }),
                  ),
                onError: (error) => toast.error(error.message),
              })
            }
          >
            <SunIcon className="mr-2 h-4 w-4" />
            <T context="queue row action: end the snooze so the task rejoins the queue now">
              Wake now
            </T>
          </DropdownMenuItem>
        )}
        {section === 'upcoming' && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              <T context="queue row menu group label">Priority</T>
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={row.priority}
              onValueChange={(value: string) => {
                const priority = QUEUE_PRIORITIES.find((p) => p === value);
                if (priority && priority !== row.priority)
                  changePriority(
                    priority,
                    gt('Priority set to {priority}.', {
                      context: 'toast after changing a queued task priority',
                      priority: m(PRIORITY_LABELS[priority]),
                    }),
                  );
              }}
            >
              {QUEUE_PRIORITIES.map((priority) => (
                <DropdownMenuRadioItem
                  key={priority}
                  closeOnClick
                  value={priority}
                  disabled={setPriority.isPending}
                >
                  {m(PRIORITY_LABELS[priority])}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
